import { useSearchParams } from 'react-router-dom';
import { Box, Chip, Grid, TableCell, TableRow } from '@mui/material';
import { FC, Fragment, Reducer, useCallback, useEffect, useMemo, useReducer } from 'react';
// components
import { Alert } from 'components/common/Alert';
import Selector from 'components/common/Selector';
import TableLoader from 'components/common/TableLoader';
import NoDataFound from 'components/common/NoDataFound';
import TableContainer from 'components/common/TableContainer';
import TableComponent from 'components/common/TableComponent';
import TableSearchField from 'components/common/TableSearchField';
import TableTabsComponent from 'components/common/TableTabsComponent';
import ActionMenuDropdown from 'components/common/ActionMenuDropdown';
// constants, reducers, graphql, styles
import {
  Scope,
  SupplierStatus,
  SuppliersPayload,
  useUpdateSupplierMutation,
  useFindTableSuppliersLazyQuery,
} from 'generated/graphql';
import {
  EDIT_TEXT,
  SCOPE_TEXT,
  HTTP_STATUS,
  SCOPE_MAPPED,
  SUPPLIER_TABS,
  DUPLICATE_TEXT,
  TAB_QUERY_TYPE,
  LOADING_TABLE_ROWS,
  EDIT_SUPPLIER_ROUTE,
  DUPLICATE_SUPPLIER_ROUTE,
  ADMIN_SUPPLIER_TABLE_HEADER,
  SUPER_SUPPLIER_TABLE_HEADER,
} from 'constants/index';
import { formatValue, isAdmin } from 'lib/helper';
import { useAuthContext } from 'contexts/AuthContext';
import { SupplierTabType } from 'interfaces/TabTypes';
import { textWhiteSpace } from 'styles/commonComponentStyle';
import { Action, ActionType, State, initialState, supplierReducer } from 'reducer/supplierReducer';

const SupplierTable: FC = () => {
  const [state, dispatch] = useReducer<Reducer<State, Action>>(supplierReducer, initialState);
  const { page, rowsPerPage, search, count, data, activeTab, scope } = state;

  const { userRoles } = useAuthContext();
  const admin = isAdmin(userRoles);

  const [searchParams] = useSearchParams();
  const tab = searchParams.get(TAB_QUERY_TYPE);

  const [findAllSuppliers, { loading: fetchLoading, error }] = useFindTableSuppliersLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { findAllSuppliers } = data;
      const { pagination, response, data: supplierData } = findAllSuppliers || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        const { page, totalCount } = pagination || {};
        dispatch({ type: ActionType.SET_PAGE, page: page || 1 });
        dispatch({ type: ActionType.SET_COUNT, count: totalCount || 0 });
        dispatch({ type: ActionType.SET_DATA, data: supplierData as SuppliersPayload['data'] });
      } else {
        resetPage();
      }
    },

    onError: () => {
      resetPage();
    },
  });

  const [updateSupplierStatus, { loading: updateLoading }] = useUpdateSupplierMutation({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { updateSupplier } = data;
      const { response } = updateSupplier || {};
      const { message, status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        Alert.success(message || '');
        fetchSupplier();
      } else {
        Alert.error(message ?? '');
      }
    },

    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  const resetPage = () => {
    dispatch({ type: ActionType.SET_PAGE, page: 1 });
    dispatch({ type: ActionType.SET_COUNT, count: 0 });
    dispatch({ type: ActionType.SET_DATA, data: [] });
  };

  const fetchSupplier = useCallback(async () => {
    await findAllSuppliers({
      variables: {
        findAllSuppliersInput: {
          paginationOptions: {
            limit: rowsPerPage,
            page,
          },
          search,
          ...(admin ? { scope } : { scope: Scope.Global }),
          ...(activeTab !== 'all' && { status: activeTab }),
        },
      },
    });
  }, [findAllSuppliers, rowsPerPage, page, search, admin, scope, activeTab]);

  useEffect(() => {
    (!search.length || search.length > 2) && fetchSupplier();
  }, [fetchSupplier, search.length]);

  const tabHandler = (_: React.SyntheticEvent<Element, Event>, value: string) => {
    dispatch({ type: ActionType.SET_ACTIVE_TAB, activeTab: value as SupplierTabType });
  };

  const onActiveHandler = async (id: string, status: boolean) => {
    await updateSupplierStatus({
      variables: {
        updateSupplierInput: {
          id,
          status: status ? SupplierStatus.Inactive : SupplierStatus.Active,
        },
      },
    });
  };

  useMemo(() => {
    if (tab === 'all') dispatch({ type: ActionType.SET_ACTIVE_TAB, activeTab: 'all' });
  }, [tab]);

  const loading = fetchLoading || updateLoading;

  const noData = Boolean((!fetchLoading && data?.length === 0) || error);

  const onScopeChange = (scope: string) => {
    dispatch({ type: ActionType.SET_PAGE, page: 1 });
    dispatch({ type: ActionType.SET_SCOPE, scope: scope as Scope });
  };

  const onSearch = (val: string) => {
    dispatch({ type: ActionType.SET_PAGE, page: 1 });
    dispatch({ type: ActionType.SET_SEARCH, search: val });
  };

  const headers = useMemo(() => {
    if (admin) {
      return ADMIN_SUPPLIER_TABLE_HEADER;
    }
    return SUPER_SUPPLIER_TABLE_HEADER;
  }, [admin]);

  return (
    <TableContainer>
      <TableTabsComponent activeTab={activeTab} tabsList={SUPPLIER_TABS} tabHandler={tabHandler} />
      <Box m={({ spacing }) => spacing(2, 3)}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TableSearchField
              withSpacing={false}
              search={search}
              onClear={() => dispatch({ type: ActionType.SET_SEARCH, search: '' })}
              onChange={onSearch}
            />
          </Grid>
          {admin && (
            <Grid item xs={12} md={4}>
              <Selector
                id="scope"
                value={scope}
                title={SCOPE_TEXT}
                options={SCOPE_MAPPED}
                onChange={onScopeChange}
              />
            </Grid>
          )}
        </Grid>
      </Box>

      <TableComponent
        noData={noData}
        page={page - 1}
        count={count}
        rowsPerPage={rowsPerPage}
        tableHeader={headers}
        setPage={(p: number) => dispatch({ type: ActionType.SET_PAGE, page: p + 1 })}
        setRowsPerPage={(r: number) => dispatch({ type: ActionType.SET_ROWS_PER_PAGE, rowsPerPage: r })}>
        {loading ? (
          <TableLoader columns={headers?.length} rows={LOADING_TABLE_ROWS} />
        ) : (
          <Fragment>
            {data?.map((cell) => {
              const { id, name: companyName, status, contact, organizationId } = cell || {};
              const { name, designation } = contact || {};

              return (
                <TableRow key={id}>
                  <TableCell sx={textWhiteSpace}>{companyName ?? '--'}</TableCell>
                  <TableCell>{name ?? '--'}</TableCell>
                  <TableCell>{designation ?? '--'}</TableCell>
                  {admin && (
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={formatValue(organizationId ? Scope.Local : Scope.Global)}
                        color={organizationId ? 'info' : 'primary'}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <ActionMenuDropdown
                      id={id ?? ''}
                      isActive={status === SupplierStatus.Active}
                      editRoute={!organizationId && admin ? DUPLICATE_SUPPLIER_ROUTE : EDIT_SUPPLIER_ROUTE}
                      editText={!organizationId && admin ? DUPLICATE_TEXT : EDIT_TEXT}
                      onActiveHandler={
                        admin ? (!!organizationId ? onActiveHandler : undefined) : onActiveHandler
                      }
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </Fragment>
        )}
      </TableComponent>

      <NoDataFound noData={noData} />
    </TableContainer>
  );
};

export default SupplierTable;
